import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Paper,
  Alert,
  Tooltip,
  CircularProgress,
  LinearProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  Download as DownloadIcon,
  Schedule as ScheduleIcon,
  HourglassEmpty as HourglassIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import JSZip from 'jszip';
import { friendlyDownloadPath, friendlySaveAsName } from './batchDownloadNames';

// Special-case display for the `check_batch_status` plugin. Renders a friendly,
// customer-safe status card with a dedicated in-place recheck + optional polling,
// instead of dumping the raw JSON.

type BatchStatusColor = 'success' | 'warning' | 'info' | 'error' | 'default';

const BATCH_STATUS_META: Record<string, { color: BatchStatusColor; label: string }> = {
  completed: { color: 'success', label: 'Completed' },
  completed_with_errors: { color: 'warning', label: 'Completed with errors' },
  processing: { color: 'info', label: 'Processing' },
  failed: { color: 'error', label: 'Failed' },
  cancelled: { color: 'default', label: 'Cancelled' },
  expired: { color: 'default', label: 'Expired' },
  unknown: { color: 'default', label: 'Unknown' },
};

/** True when a node's output looks like a `check_batch_status` result. */
export const isBatchStatusOutput = (output: any): boolean =>
  !!output &&
  typeof output === 'object' &&
  typeof output.status === 'string' &&
  'succeeded' in output &&
  ('result_count' in output || 'output_ready' in output || 'done' in output);

const formatTimestamp = (iso?: string): string | null => {
  if (!iso || typeof iso !== 'string') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const collectResultFiles = (output: any): { filename: string; url: string }[] => {
  const files: { filename: string; url: string }[] = [];
  if (Array.isArray(output?.result_files)) {
    output.result_files.forEach((f: any) => {
      if (f?.download_url) files.push({ filename: f.filename || 'result.jsonl', url: f.download_url });
    });
  }
  if (files.length === 0 && typeof output?.output_download_url === 'string') {
    files.push({ filename: 'result.jsonl', url: output.output_download_url });
    Object.keys(output)
      .filter((k) => /^output_download_url_\d+$/.test(k))
      .sort()
      .forEach((k) => files.push({ filename: `result_${k.split('_').pop()}.jsonl`, url: output[k] }));
  }
  return files;
};

/** The original submitted input.jsonl, if the status exposes it. */
const collectInputFile = (output: any): { filename: string; url: string } | null => {
  if (output?.input_file?.download_url) {
    return { filename: output.input_file.filename || 'input.jsonl', url: output.input_file.download_url };
  }
  if (typeof output?.input_download_url === 'string') {
    return { filename: 'input.jsonl', url: output.input_download_url };
  }
  return null;
};

const fileDownloadMeta = (filename: string): { label: string; title: string; primary: boolean } => {
  if (filename === 'run_summary.json') {
    return {
      label: 'Run summary',
      title: 'Status, credits, and a rollup of warnings/errors for this run',
      primary: false,
    };
  }
  if (filename === 'records.json') {
    return {
      label: 'Clean results (JSON)',
      title: 'Parsed JSON array of feed records — ready to use',
      primary: true,
    };
  }
  if (filename === 'records.jsonl') {
    return {
      label: 'Results (JSONL)',
      title: 'Same records, one JSON object per line',
      primary: false,
    };
  }
  if (filename === 'predictions.jsonl' || filename.startsWith('predictions')) {
    return {
      label: 'Raw batch output (debug)',
      title: 'Provider envelope (OpenAI/Vertex) before unwrap — for debugging',
      primary: false,
    };
  }
  if (filename === 'errors.jsonl') {
    return {
      label: 'Batch errors (debug)',
      title: 'Provider error file',
      primary: false,
    };
  }
  return {
    label: `Output: ${filename}`,
    title: filename,
    primary: false,
  };
};

const tidyStatusMessage = (raw: string): string => {
  if (!raw) return '';
  return raw
    .replace(/\s*Download records\.json[^.]*(?:\.|$)/gi, '')
    .replace(/\s*records\.jsonl\s*\/\s*predictions\.jsonl[^.]*\./gi, '')
    .replace(/\s*\d+\s+row\(s\) flagged ERR_DUP_SKU[^.]*\./gi, '')
    .trim();
};

const issuesFromOutput = (output: any): any[] => {
  if (Array.isArray(output?.issues) && output.issues.length) return output.issues;
  if (Array.isArray(output?.run_summary?.issues) && output.run_summary.issues.length) {
    return output.run_summary.issues;
  }
  const dups = output?.duplicate_skus;
  if (Array.isArray(dups) && dups.length) {
    return [{
      code: 'ERR_DUP_SKU',
      severity: 'error',
      count: dups.length,
      examples: dups.slice(0, 8).map((id: string) => ({ id })),
    }];
  }
  return [];
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(a.href);
  document.body.removeChild(a);
};

const fetchBatchFile = async (url: string, getToken: () => Promise<string | undefined>): Promise<Blob | null> => {
  try {
    const token = await getToken();
    const res = await fetch(`/api/v2/chains/download-proxy?url=${encodeURIComponent(url)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.ok) return await res.blob();
  } catch { /* fall through */ }
  try {
    const res = await fetch(url);
    if (res.ok) return await res.blob();
  } catch { /* ignore */ }
  return null;
};

const POLL_INTERVAL_MS = 15_000;

export const BatchStatusPanel: React.FC<{
  output: any;
  nodeInput: any;
  getToken: () => Promise<string | undefined>;
  onUpdate: (fresh: any) => void;
  executionId?: string;
  nodeId?: string;
}> = ({ output, nodeInput, getToken, onUpdate, executionId, nodeId }) => {
  const batchJobName: string | undefined = nodeInput?.batch_job_name;
  const outputUri: string | undefined = nodeInput?.output_uri;
  const canCheck = typeof batchJobName === 'string' && batchJobName.length > 0;

  const [checking, setChecking] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  // Poll automatically while the job is still running (opt-out via the toggle).
  const [autoRefresh, setAutoRefresh] = useState<boolean>(() => canCheck && !output?.done);
  const [error, setError] = useState<string | null>(null);
  const [lastLocalCheck, setLastLocalCheck] = useState<string | null>(null);

  const status: string = output?.status || 'unknown';
  const meta = BATCH_STATUS_META[status] || BATCH_STATUS_META.unknown;
  const done = !!output?.done;
  const succeeded = !!output?.succeeded;
  const resultCount = Number(output?.result_count ?? 0);
  const message: string = output?.message || '';
  const checkedAt = formatTimestamp(output?.checked_at) || lastLocalCheck;
  const files = useMemo(() => collectResultFiles(output), [output]);
  const inputFile = useMemo(() => collectInputFile(output), [output]);
  const summaryFile = useMemo(() => files.find((f) => f.filename === 'run_summary.json') || null, [files]);
  const resultFiles = useMemo(() => files.filter((f) => f.filename !== 'run_summary.json'), [files]);
  const issues = useMemo(() => issuesFromOutput(output), [output]);
  const displayMessage = tidyStatusMessage(message);
  const hasErrorIssue = issues.some((i) => i?.severity === 'error');
  const borderColor = meta.color === 'default' ? 'grey.400' : `${meta.color}.main`;

  const runCheck = useCallback(async () => {
    if (!canCheck) return;
    setChecking(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch('/api/v2/plugins/check_batch_status/execute', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          input_data: { batch_job_name: batchJobName, output_uri: outputUri },
          chain_execution_id: executionId,
          node_id: nodeId,
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `Check failed (${res.status})`);
      }
      const json = await res.json();
      const fresh = json?.data ?? json;
      setLastLocalCheck(new Date().toLocaleString());
      onUpdate(fresh);
    } catch (e: any) {
      setError(e?.message || 'Status check failed');
    } finally {
      setChecking(false);
    }
  }, [canCheck, batchJobName, outputUri, getToken, onUpdate, executionId, nodeId]);

  const allBatchFiles = useMemo(
    () => [...(inputFile ? [inputFile] : []), ...files],
    [inputFile, files],
  );

  const downloadOne = useCallback(async (url: string, filename: string) => {
    setDownloading(url);
    try {
      const blob = await fetchBatchFile(url, getToken);
      if (!blob) {
        window.open(url, '_blank');
        return;
      }
      triggerBlobDownload(blob, filename);
    } finally {
      setDownloading(null);
    }
  }, [getToken]);

  const downloadAll = useCallback(async () => {
    if (allBatchFiles.length === 0) return;
    setZipping(true);
    try {
      const zip = new JSZip();
      const used = new Set<string>();
      let added = 0;
      for (const f of allBatchFiles) {
        const blob = await fetchBatchFile(f.url, getToken);
        if (!blob) continue;
        const path = friendlyDownloadPath(f.filename || f.url) || friendlySaveAsName(f.filename || f.url);
        let name = path;
        if (used.has(name)) {
          const dot = name.lastIndexOf('.');
          const slash = name.lastIndexOf('/');
          const extStart = dot > slash ? dot : name.length;
          let n = 2;
          let candidate = `${name.slice(0, extStart)}_${n}${name.slice(extStart)}`;
          while (used.has(candidate)) {
            n += 1;
            candidate = `${name.slice(0, extStart)}_${n}${name.slice(extStart)}`;
          }
          name = candidate;
        }
        used.add(name);
        zip.file(name, blob);
        added += 1;
      }
      if (!Array.from(used).some((n) => n.endsWith('00_RUN_SUMMARY.json') || n === 'run_summary.json')) {
        const summary = output?.run_summary || {
          title: 'Batch run summary',
          status: output?.status,
          succeeded: output?.succeeded,
          checked_at: output?.checked_at,
          model: output?.model || output?.model_used,
          credits: output?.ai_credits_used,
          tokens: output?.token_usage,
          issues,
          duplicate_skus: output?.duplicate_skus || [],
        };
        zip.file('00_RUN_SUMMARY.json', JSON.stringify(summary, null, 2));
        added += 1;
      }
      if (added === 0) return;
      const content = await zip.generateAsync({ type: 'blob' });
      triggerBlobDownload(content, 'batch_results.zip');
    } finally {
      setZipping(false);
    }
  }, [allBatchFiles, getToken, output, issues]);

  const downloadRunSummary = useCallback(async () => {
    if (summaryFile) {
      await downloadOne(summaryFile.url, friendlySaveAsName(summaryFile.filename || summaryFile.url));
      return;
    }
    const summary = output?.run_summary || {
      title: 'Batch run summary',
      status: output?.status,
      succeeded: output?.succeeded,
      checked_at: output?.checked_at,
      model: output?.model || output?.model_used,
      credits: output?.ai_credits_used,
      tokens: output?.token_usage,
      issues,
      duplicate_skus: output?.duplicate_skus || [],
    };
    triggerBlobDownload(
      new Blob([JSON.stringify(summary, null, 2)], { type: 'application/json' }),
      '00_RUN_SUMMARY.json',
    );
  }, [summaryFile, downloadOne, output, issues]);

  // Check immediately on open so a freshly-opened panel reports the current
  // state right away (done or not) instead of waiting a full poll interval.
  useEffect(() => {
    if (canCheck) runCheck();
    // Mount-only: intentionally not re-run when runCheck identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-poll while the job is still running (stops itself once terminal). The
  // interval only fires subsequent checks; the first check is the mount one above.
  useEffect(() => {
    if (!autoRefresh || done) return;
    const id = setInterval(() => { runCheck(); }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [autoRefresh, done, runCheck]);

  return (
    <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', borderColor, borderWidth: 1.5 }}>
      {(checking || (autoRefresh && !done)) && <LinearProgress />}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1 }}>
            Batch job status
          </Typography>
          <Chip
            icon={done ? (succeeded ? <SuccessIcon /> : <ErrorIcon />) : <HourglassIcon />}
            label={meta.label}
            color={meta.color}
            size="small"
          />
        </Box>

        {displayMessage && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {displayMessage}
          </Typography>
        )}

        {issues.length > 0 && (
          <Alert
            severity={hasErrorIssue ? 'warning' : 'info'}
            icon={<WarningIcon />}
            sx={{ mb: 1.5 }}
            action={
              <Button color="inherit" size="small" onClick={downloadRunSummary} sx={{ textTransform: 'none' }}>
                Download summary
              </Button>
            }
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Issues on this run
            </Typography>
            {issues.map((iss, idx) => {
              const ids = (iss.examples || [])
                .map((ex: any) => ex?.id)
                .filter(Boolean)
                .slice(0, 5);
              return (
                <Typography key={`${iss.code}-${idx}`} variant="body2">
                  <strong>{iss.code}</strong>
                  {iss.count ? ` × ${iss.count}` : ''}
                  {ids.length ? ` — ${ids.join(', ')}` : ''}
                  {iss.examples?.[0]?.message && !ids.length ? ` — ${iss.examples[0].message}` : ''}
                </Typography>
              );
            })}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Full detail is in 00_RUN_SUMMARY.json. Flagged rows are also marked in 01_CLEAN_RESULTS.json.
            </Typography>
          </Alert>
        )}

        {done && issues.length === 0 && (
          <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 1.5 }}>
            No warnings or errors flagged on this run.
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Chip size="small" variant="outlined" label={done ? 'Done' : 'In progress'} color={done ? 'success' : 'info'} />
          {done && (
            <Chip size="small" variant="outlined" label={succeeded ? 'Succeeded' : 'Not succeeded'} color={succeeded ? 'success' : 'error'} />
          )}
          {resultCount > 0 && (
            <Chip size="small" variant="outlined" label={`${resultCount} result file${resultCount === 1 ? '' : 's'}`} />
          )}
          {typeof output?.ai_credits_used === 'number' && output.ai_credits_used > 0 && (
            <Chip size="small" variant="outlined" color="primary" label={`${Number(output.ai_credits_used).toFixed(2)} credits`} />
          )}
          {output?.token_usage?.total_tokens > 0 && (
            <Chip
              size="small"
              variant="outlined"
              label={`${Number(output.token_usage.total_tokens).toLocaleString()} tokens`}
            />
          )}
          {Array.isArray(output?.duplicate_skus) && output.duplicate_skus.length > 0 && (
            <Chip size="small" color="warning" variant="outlined" label={`${output.duplicate_skus.length} duplicate SKU${output.duplicate_skus.length === 1 ? '' : 's'}`} />
          )}
        </Box>

        {(inputFile || files.length > 0) && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Open <strong>01_CLEAN_RESULTS.json</strong> for the finished feed.
              {' '}<strong>00_RUN_SUMMARY.json</strong> has warnings, credits, and run info.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {allBatchFiles.length > 1 && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={zipping ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                  disabled={zipping || !!downloading}
                  onClick={downloadAll}
                  sx={{ textTransform: 'none' }}
                >
                  {zipping ? 'Zipping…' : `Download all (${allBatchFiles.length} files)`}
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={downloading === (summaryFile?.url || 'summary') ? <CircularProgress size={14} /> : <DownloadIcon />}
                disabled={zipping || !!downloading}
                onClick={downloadRunSummary}
                sx={{ textTransform: 'none' }}
              >
                Run summary
              </Button>
              {inputFile && (
                <Tooltip title="Saves as debug/00_original_input.jsonl in the zip; the exact JSONL submitted to the batch job" arrow>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={downloading === inputFile.url ? <CircularProgress size={14} /> : <DownloadIcon />}
                    disabled={zipping || !!downloading}
                    onClick={() => downloadOne(inputFile.url, friendlySaveAsName(inputFile.filename || inputFile.url))}
                    sx={{ textTransform: 'none' }}
                  >
                    Original input
                  </Button>
                </Tooltip>
              )}
              {resultFiles.map((f, i) => {
                const meta = fileDownloadMeta(f.filename);
                return (
                  <Tooltip key={`dl-${i}`} title={`${meta.title} — saves as ${friendlySaveAsName(f.filename || f.url)}`} arrow>
                    <Button
                      size="small"
                      variant={meta.primary ? 'contained' : 'outlined'}
                      startIcon={downloading === f.url ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                      disabled={zipping || !!downloading}
                      onClick={() => downloadOne(f.url, friendlySaveAsName(f.filename || f.url))}
                      sx={{ textTransform: 'none' }}
                    >
                      {meta.label}
                    </Button>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={checking ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={runCheck}
            disabled={!canCheck || checking}
            sx={{ textTransform: 'none' }}
          >
            {checking ? 'Checking…' : 'Check now'}
          </Button>
          <FormControlLabel
            control={<Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} disabled={!canCheck || done} />}
            label={<Typography variant="caption">Auto-refresh every 15s</Typography>}
          />
          <Box sx={{ flexGrow: 1 }} />
          {checkedAt && (
            <Tooltip title="Time of the most recent status check" arrow>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                <ScheduleIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption">Last checked: {checkedAt}</Typography>
              </Box>
            </Tooltip>
          )}
        </Box>

        {!canCheck && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            No job handle on this step&apos;s input — re-run the chain from the submit step to get a checkable job.
          </Typography>
        )}
      </Box>
    </Paper>
  );
};
